import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Video, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [tiktok, setTiktok] = useState("");
  const [instagram, setInstagram] = useState("");
  const [shopee, setShopee] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [platform, setPlatform] = useState("");
  const [username, setUsername] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!role) return;
    if (!name.trim()) return toast.error("Nama wajib diisi");
    if (!tiktok.trim() || !/tiktok/i.test(tiktok)) return toast.error("Masukkan username / link akun TikTok yang valid");
    if (!instagram.trim() || !/instagram/i.test(instagram)) return toast.error("Masukkan username / link akun Instagram yang valid");
    if (!whatsapp.trim()) return toast.error("Nomor WhatsApp wajib diisi");
    if (!city.trim()) return toast.error("Kota / domisili wajib diisi");
    if (role === "affiliator" && (!platform || !username)) {
      toast.error("Lengkapi platform & username affiliate kamu");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/auth/role", {
        role,
        name: name.trim(),
        tiktok_url: tiktok.trim(),
        instagram_url: instagram.trim(),
        shopee_url: shopee.trim(),
        whatsapp: whatsapp.trim(),
        city: city.trim(),
        address: address.trim(),
        ecommerce_platform: platform || null,
        ecommerce_username: username || null,
      });
      setUser(res.data);
      toast.success("Selamat datang di Showtynion!");
      navigate(role === "affiliator" ? "/affiliator" : "/creator", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan peran");
    } finally {
      setSaving(false);
    }
  };

  const isGmail = (user?.email || "").toLowerCase().endsWith("@gmail.com");

  return (
    <div className="min-h-screen bg-showty-cream showty-grain flex items-center justify-center px-6 py-16" data-testid="onboarding-page">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-10">
          <Logo className="w-14 h-14 mx-auto" />
          <h1 className="mt-5 font-display text-4xl lg:text-5xl text-showty-navy">Pilih peran kamu</h1>
          <p className="mt-3 text-sm text-muted-foreground">Jalur ini menentukan sample & fitur yang bisa kamu akses. Peran affiliator terpisah dari beauty creator.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {[
            { id: "beauty_creator", icon: Crown, title: "Beauty Creator", desc: "Konten review & tutorial. Barter 2 TikTok + IG owning content.", ring: "showty-red" },
            { id: "affiliator", icon: Video, title: "Affiliator", desc: "Affiliate Shopee/TikTok. Barter 1 TikTok + Live.", ring: "showty-navy" },
          ].map((r) => (
            <motion.button
              key={r.id}
              onClick={() => setRole(r.id)}
              whileHover={{ y: -4 }}
              data-testid={`role-${r.id}`}
              className={`text-left rounded-2xl border-2 bg-white p-7 transition-colors ${role === r.id ? "border-showty-red" : "border-border hover:border-showty-gold/60"}`}
            >
              <r.icon className={`w-8 h-8 ${r.id === "beauty_creator" ? "text-showty-red" : "text-showty-navy"}`} />
              <h3 className="mt-4 font-display text-2xl text-showty-navy">{r.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{r.desc}</p>
            </motion.button>
          ))}
        </div>

        {role && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-2xl border border-border bg-white p-7 space-y-4" data-testid="onboarding-details">
            {!isGmail && (
              <div className="rounded-lg bg-showty-red/10 border border-showty-red/30 px-3 py-2 text-sm text-showty-red" data-testid="gmail-warning">
                Akun kamu harus memakai Gmail asli (@gmail.com). Silakan daftar/masuk ulang dengan akun Gmail.
              </div>
            )}
            <div>
              <Label>Nama lengkap</Label>
              <Input className="mt-1.5" placeholder="Nama sesuai profil" value={name} onChange={(e) => setName(e.target.value)} data-testid="onboarding-name" />
            </div>
            <div>
              <Label>Username / Link akun TikTok</Label>
              <Input className="mt-1.5" placeholder="https://www.tiktok.com/@username" value={tiktok} onChange={(e) => setTiktok(e.target.value)} data-testid="onboarding-tiktok" />
            </div>
            <div>
              <Label>Username / Link akun Instagram</Label>
              <Input className="mt-1.5" placeholder="https://www.instagram.com/username" value={instagram} onChange={(e) => setInstagram(e.target.value)} data-testid="onboarding-instagram" />
            </div>
            <div>
              <Label>Username Shopee (opsional)</Label>
              <Input className="mt-1.5" placeholder="username / nama toko Shopee" value={shopee} onChange={(e) => setShopee(e.target.value)} data-testid="onboarding-shopee" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Nomor WhatsApp</Label>
                <Input className="mt-1.5" placeholder="08xxxxxxxxxx" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} data-testid="onboarding-whatsapp" />
              </div>
              <div>
                <Label>Kota / Domisili</Label>
                <Input className="mt-1.5" placeholder="cth: Bandung" value={city} onChange={(e) => setCity(e.target.value)} data-testid="onboarding-city" />
              </div>
            </div>
            <div>
              <Label>Alamat lengkap (untuk pengiriman)</Label>
              <Input className="mt-1.5" placeholder="Nama jalan, nomor rumah, kelurahan, kecamatan" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="onboarding-address" />
            </div>
            {role === "affiliator" && (
              <>
                <div>
                  <Label>Platform e-commerce</Label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger className="mt-1.5" data-testid="affiliator-platform"><SelectValue placeholder="Pilih platform" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="shopee">Shopee</SelectItem>
                      <SelectItem value="tiktok">TikTok Shop</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Username / akun affiliate</Label>
                  <Input className="mt-1.5" placeholder="@username" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="affiliator-username" />
                </div>
              </>
            )}
          </motion.div>
        )}

        <div className="mt-8 flex justify-center">
          <Button data-testid="onboarding-submit" onClick={submit} disabled={!role || saving} size="lg" className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white px-10 group">
            {saving ? "Menyimpan…" : "Lanjut"} <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </div>
  );
}
